/**
 * A picture small enough for the room's chat.
 *
 * The rule is 50 by 50 and a few tens of kilobytes, and it exists so that a chat carrying pictures
 * does not become a place people store pictures. A still image is shrunk here, in the browser, so
 * nothing large is ever uploaded; a GIF is passed through untouched, because re-drawing it on a
 * canvas would keep one frame and throw the animation away — so a GIF that is already too big is
 * refused rather than silently flattened.
 *
 * The server checks all of this again, from the file's own bytes. This is the courtesy, not the
 * rule.
 */

export const PICTURE_RULES = {
  /** The widest and tallest the server accepts. */
  maxPixels: 64,
  /** What a still image is shrunk to. */
  size: 50,
  maxBytes: 64 * 1024,
  types: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
} as const

export interface ChatPicture {
  dataUrl: string
  width: number
  height: number
  bytes: number
  animated: boolean
}

/** Why a picture was refused, as a translation key — or null when it was fine. */
export type PictureProblem = 'type' | 'tooBig' | 'unreadable'

export async function prepareChatPicture(file: File): Promise<ChatPicture | PictureProblem> {
  if (!PICTURE_RULES.types.includes(file.type as (typeof PICTURE_RULES.types)[number])) return 'type'

  if (file.type === 'image/gif') {
    // Kept as it is, animation and all. The size is the price of moving.
    if (file.size > PICTURE_RULES.maxBytes) return 'tooBig'
    const read = await readSize(file)
    if (!read) return 'unreadable'
    if (read.width > PICTURE_RULES.maxPixels || read.height > PICTURE_RULES.maxPixels) return 'tooBig'
    return { dataUrl: await asDataUrl(file), width: read.width, height: read.height, bytes: file.size, animated: true }
  }

  const url = URL.createObjectURL(file)
  try {
    const image = await load(url)
    const scale = Math.min(1, PICTURE_RULES.size / image.naturalWidth, PICTURE_RULES.size / image.naturalHeight)
    const width = Math.max(1, Math.round(image.naturalWidth * scale))
    const height = Math.max(1, Math.round(image.naturalHeight * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) return 'unreadable'
    context.drawImage(image, 0, 0, width, height)

    // PNG keeps a drawing's edges and a screenshot's text crisp at this size, where JPEG smears them.
    const dataUrl = canvas.toDataURL('image/png')
    const bytes = Math.ceil(((dataUrl.length - dataUrl.indexOf(',') - 1) * 3) / 4)
    if (bytes > PICTURE_RULES.maxBytes) return 'tooBig'

    return { dataUrl, width, height, bytes, animated: false }
  } catch {
    return 'unreadable'
  } finally {
    URL.revokeObjectURL(url)
  }
}

function load(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('unreadable'))
    image.src = url
  })
}

async function readSize(file: File): Promise<{ width: number; height: number } | null> {
  const url = URL.createObjectURL(file)
  try {
    const image = await load(url)
    return { width: image.naturalWidth, height: image.naturalHeight }
  } catch {
    return null
  } finally {
    URL.revokeObjectURL(url)
  }
}

function asDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('unreadable'))
    reader.readAsDataURL(file)
  })
}
